'use client';

import { subagentStreamRoute, type SubagentStreamEvent } from '@/app/api/subagents/[id]/stream/route.def';
import { AbortError } from '@/domains/common/abort';
import { streamApi, type StreamHandle } from '@/domains/common/api-client';
import { useEffect, useRef, type ReactNode } from 'react';

const TAG = 'subagent-terminal';
const LIVE_TAG_PREFIX = 'subagent-live:';

interface SubagentWatchHandlers {
  appendNote: (note: { agent: string; body: ReactNode; tag?: string }) => void;
  clearNotes: (tag: string) => void;
}

export function useSubagentTerminalWatch({
  appendNote,
  clearNotes,
}: SubagentWatchHandlers): (id: string) => void {
  const watched = useRef<Set<string>>(new Set());
  const handles = useRef<StreamHandle<SubagentStreamEvent>[]>([]);
  const appendRef = useRef(appendNote);
  const clearRef = useRef(clearNotes);
  const liveStatusRef = useRef<Map<string, string>>(new Map());
  appendRef.current = appendNote;
  clearRef.current = clearNotes;

  useEffect(
    () => () => {
      handles.current.forEach(h => h.stop());
      handles.current = [];
    },
    [],
  );

  function renderLink(id: string): ReactNode {
    return (
      <a href={`/agents/${id}`} className="text-acc hover:underline">
        Open it
      </a>
    );
  }

  function replaceLiveNote(id: string, statusKey: string, body: ReactNode): void {
    if (liveStatusRef.current.get(id) === statusKey) return;
    liveStatusRef.current.set(id, statusKey);
    const tag = `${LIVE_TAG_PREFIX}${id}`;
    clearRef.current(tag);
    appendRef.current({ agent: 'Matilda', body, tag });
  }

  function clearLiveNote(id: string): void {
    liveStatusRef.current.delete(id);
    clearRef.current(`${LIVE_TAG_PREFIX}${id}`);
  }

  function terminalBody(id: string, status: string, failureReason: string | null): ReactNode {
    if (status === 'completed') {
      return <>Sub-agent finished and reported back. {renderLink(id)}</>;
    }
    return (
      <>
        A sub-agent you started {status === 'aborted' ? 'was stopped' : 'failed'}
        {failureReason ? `: ${failureReason}` : '.'} {renderLink(id)}
      </>
    );
  }

  return (id: string): void => {
    if (watched.current.has(id)) return;
    watched.current.add(id);
    replaceLiveNote(id, 'watching', <>Waiting on a sub-agent to report back. {renderLink(id)}</>);
    const handle = streamApi(subagentStreamRoute, { params: { id }, query: {} });
    handles.current.push(handle);
    void (async () => {
      try {
        for await (const frame of handle.data) {
          const ev = frame.data;
          if (ev.kind === 'status' && (ev.status === 'spawning' || ev.status === 'running')) {
            replaceLiveNote(
              id,
              ev.status,
              <>Sub-agent is running. Matilda will post back here when it finishes. {renderLink(id)}</>,
            );
          } else if (ev.kind === 'status' && ev.status === 'awaiting_input') {
            replaceLiveNote(
              id,
              ev.status,
              <>Sub-agent is waiting for input in its thread. {renderLink(id)}</>,
            );
          } else if (
            ev.kind === 'status' &&
            (ev.status === 'completed' || ev.status === 'failed' || ev.status === 'aborted')
          ) {
            clearLiveNote(id);
            appendRef.current({
              agent: 'Matilda',
              body: terminalBody(id, ev.status, ev.failure_reason),
              tag: TAG,
            });
            handle.stop();
            return;
          }
          if (ev.kind === 'end') {
            if (ev.reason === 'no_live_runtime') {
              replaceLiveNote(
                id,
                ev.reason,
                <>
                  Waiting on a sub-agent running in the background. Matilda will post back here when it
                  finishes. {renderLink(id)}
                </>,
              );
            }
            return;
          }
        }
      } catch (e) {
        AbortError.rethrowIfNotAbort(e);
      }
    })();
  };
}
